import { motion } from 'framer-motion'
import { useAnimationConfig } from '../hooks/useReducedMotion'

interface GlassButtonProps {
  children: React.ReactNode
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  className?: string
  title?: string
}

/**
 * Glass Button Component
 * Soft-UI styled button with glassmorphism
 * 
 * Phase 5.3: Floating UI
 */
export function GlassButton({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  className = '',
  title,
}: GlassButtonProps) {
  const animConfig = useAnimationConfig()

  const sizeStyles = {
    sm: { padding: '6px 12px', fontSize: '12px' },
    md: { padding: '10px 20px', fontSize: '14px' },
    lg: { padding: '14px 28px', fontSize: '16px' },
  }

  const variantStyles = {
    primary: {
      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(59, 130, 246, 0.7))',
      color: 'white',
      border: '1px solid rgba(59, 130, 246, 0.5)',
    },
    secondary: {
      background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7))',
      color: '#374151',
      border: '1px solid rgba(255,255,255,0.5)',
    },
    danger: {
      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.9), rgba(239, 68, 68, 0.7))',
      color: 'white',
      border: '1px solid rgba(239, 68, 68, 0.5)',
    },
  }

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`relative rounded-xl font-medium cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      style={{
        ...sizeStyles[size],
        ...variantStyles[variant],
        boxShadow: `
          0 2px 8px rgba(0,0,0,0.08),
          inset 0 1px 0 rgba(255,255,255,0.3)
        `,
      }}
      whileHover={disabled ? {} : { scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 17,
        duration: animConfig.reducedMotion ? 0 : undefined,
      }}
    >
      {children}
    </motion.button>
  )
}
